import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Alert, Box, Button, Card, CardContent, Grid, IconButton, MenuItem, Stack, TextField, Typography } from '@mui/material';
import { Add, Delete, ArrowBack } from '@mui/icons-material';
import { fetchShipmentById, fetchShipmentCarriers, searchShipmentPackages, updateShipment } from '../../services/shipment';

export default function EditShipment() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [packageInput, setPackageInput] = useState('');
  const [searchingPackages, setSearchingPackages] = useState(false);
  const [packageResults, setPackageResults] = useState([]);
  const [error, setError] = useState('');
  const [form, setForm] = useState(null);
  const [carriers, setCarriers] = useState([]);
  const [carriersLoading, setCarriersLoading] = useState(true);
  const [shipmentSent, setShipmentSent] = useState(false);

  useEffect(() => {
    setLoading(true);
    fetchShipmentById(id).then((data) => {
      setForm({
        container_number: data.container_number || '',
        carrier: data.carrier || '',
        provider: data.provider || 'scraper',
        shipment_type: data.shipment_type || '',
        package_numbers: (data.items || []).map((x) => x.package_number).filter(Boolean),
      });
      setShipmentSent(data.is_sent === true);
      setError('');
    }).catch((err) => {
      setError(err?.response?.data?.detail || 'Failed to load shipment');
    }).finally(() => setLoading(false));
  }, [id]);

  const canSubmit = useMemo(() => (
    !!form?.provider
  ), [form]);

  const carrierOptions = useMemo(() => {
    const normalized = Array.isArray(carriers) ? [...carriers] : [];
    const currentCarrier = (form?.carrier || '').trim();

    if (currentCarrier && !normalized.some((carrier) => carrier.name === currentCarrier)) {
      normalized.unshift({
        id: `current-${currentCarrier}`,
        name: currentCarrier,
      });
    }

    return normalized;
  }, [carriers, form]);

  useEffect(() => {
    let active = true;

    const loadCarriers = async () => {
      setCarriersLoading(true);
      try {
        const data = await fetchShipmentCarriers();
        if (active) {
          setCarriers(Array.isArray(data) ? data : []);
        }
      } catch {
        if (active) {
          setCarriers([]);
        }
      } finally {
        if (active) {
          setCarriersLoading(false);
        }
      }
    };

    loadCarriers();
    return () => {
      active = false;
    };
  }, []);

  useEffect(() => {
    const query = packageInput.trim();
    if (!query) {
      setPackageResults([]);
      return;
    }

    const timer = setTimeout(async () => {
      setSearchingPackages(true);
      try {
        const results = await searchShipmentPackages(query);
        setPackageResults(results || []);
      } catch {
        setPackageResults([]);
      } finally {
        setSearchingPackages(false);
      }
    }, 300);

    return () => clearTimeout(timer);
  }, [packageInput]);

  const addPackage = (inputValue = packageInput) => {
    if (!form) return;
    if (shipmentSent) {
      setError('Cannot add packages after shipment has been marked as sent');
      return;
    }
    const packageNumber = inputValue.trim();
    if (!packageNumber) return;
    if (form.package_numbers.some((p) => p === packageNumber)) {
      setError('Package already added');
      return;
    }

    setError('');
    setForm((prev) => ({ ...prev, package_numbers: [...prev.package_numbers, packageNumber] }));
    setPackageInput('');
    setPackageResults([]);
  };

  const removePackage = (packageNumber) => {
    if (shipmentSent) return;
    setForm((prev) => ({ ...prev, package_numbers: prev.package_numbers.filter((p) => p !== packageNumber) }));
  };

  const submit = async () => {
    if (!canSubmit) return;
    setSaving(true);
    setError('');
    try {
      // Tracking-derived fields are not manually edited here; sync updates those fields.
      const payload = {
        provider: form.provider,
        shipment_type: form.shipment_type || null,
      };
      if (form.carrier) {
        payload.carrier = form.carrier;
      } else {
        payload.carrier = null;
      }
      if (!shipmentSent) {
        payload.package_numbers = form.package_numbers;
      }
      const containerNumber = form.container_number.trim();
      if (containerNumber) {
        payload.container_number = containerNumber;
      }
      await updateShipment(id, payload);
      navigate(`/admin/shipments/${id}`);
    } catch (err) {
      const packageError = err?.response?.data?.package_numbers;
      setError(
        (Array.isArray(packageError) ? packageError[0] : packageError) ||
        err?.response?.data?.detail ||
        err?.response?.data?.non_field_errors?.[0] ||
        'Failed to update shipment'
      );
    } finally {
      setSaving(false);
    }
  };

  if (loading || !form) return <Typography>Loading...</Typography>;

  return (
    <Box sx={{
      minHeight: '100vh',
      backgroundColor: 'background.default',
      px: { xs: 0.75, sm: 1, md: 1.5 },
      py: { xs: 2, sm: 2.5, md: 3 },
      maxWidth: '95vw',
      overflowX: 'hidden'
    }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
        <Typography variant="h5">Edit Shipment</Typography>
        <Button variant="outlined" startIcon={<ArrowBack />} onClick={() => navigate('/admin/shipments')}>
          Back to Shipments
        </Button>
      </Box>
      <Card>
        <CardContent>
          {!!error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

          <Grid container spacing={2}>
            <Grid size={{ xs: 12 }}>
              <TextField
                fullWidth
                label="Container Number (Optional)"
                value={form.container_number}
                onChange={(e) => setForm({ ...form, container_number: e.target.value })}
              />
            </Grid>
            <Grid size={{ xs: 12, md: 4 }}>
              <TextField
                select
                fullWidth
                label="Carrier (Optional)"
                value={form.carrier}
                onChange={(e) => setForm({ ...form, carrier: e.target.value })}
                helperText={carriersLoading ? 'Loading carriers...' : 'Manage carriers from the shipments page if needed.'}
              >
                <MenuItem value="">None</MenuItem>
                {carrierOptions.map((carrier) => (
                  <MenuItem key={carrier.id} value={carrier.name}>{carrier.name}</MenuItem>
                ))}
              </TextField>
            </Grid>
            <Grid size={{ xs: 12, md: 4 }}>
              <TextField
                select
                fullWidth
                label="Provider"
                value={form.provider}
                onChange={(e) => setForm({ ...form, provider: e.target.value })}
              >
                <MenuItem value="scraper">scraper</MenuItem>
                <MenuItem value="api">api</MenuItem>
                <MenuItem value="aggregator">aggregator</MenuItem>
                <MenuItem value="manual">manual</MenuItem>
              </TextField>
            </Grid>
            <Grid size={{ xs: 12, md: 4 }}>
              <TextField
                select
                fullWidth
                label="Shipment Type (Optional)"
                value={form.shipment_type}
                onChange={(e) => setForm({ ...form, shipment_type: e.target.value })}
              >
                <MenuItem value="">None</MenuItem>
                <MenuItem value="ocean">ocean</MenuItem>
                <MenuItem value="air">air</MenuItem>
                <MenuItem value="parcel">parcel</MenuItem>
                <MenuItem value="lcl">lcl</MenuItem>
              </TextField>
            </Grid>
          </Grid>

          <Typography variant="h6" sx={{ mt: 3, mb: 1 }}>Package Numbers</Typography>
          {shipmentSent && (
            <Alert severity="info" sx={{ mb: 2 }}>
              This shipment has been marked as sent. Packages can no longer be added or removed.
            </Alert>
          )}
          <Grid container spacing={1} sx={{ mb: 2 }}>
            <Grid size={{ xs: 12, md: 8 }}>
              <TextField
                fullWidth
                label="Enter Package Number"
                value={packageInput}
                onChange={(e) => setPackageInput(e.target.value)}
                disabled={shipmentSent}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') {
                    e.preventDefault();
                    addPackage();
                  }
                }}
              />
            </Grid>
            <Grid size={{ xs: 12, md: 4 }}>
              <Button
                fullWidth
                variant="outlined"
                startIcon={<Add />}
                onClick={() => addPackage()}
                disabled={shipmentSent || !packageInput.trim()}
                sx={{ height: '100%' }}
              >
                Add Package
              </Button>
            </Grid>
          </Grid>

          {(searchingPackages || packageResults.length > 0) && (
            <Box sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 1, mb: 2, maxHeight: 220, overflowY: 'auto' }}>
              {searchingPackages && (
                <Typography variant="body2" sx={{ p: 1.5 }}>Searching shipped packages...</Typography>
              )}
              {!searchingPackages && packageResults.map((pkg) => (
                <Box
                  key={pkg.id}
                  sx={{ p: 1.5, borderTop: '1px solid', borderColor: 'divider', cursor: 'pointer', '&:hover': { bgcolor: 'action.hover' } }}
                  onClick={() => addPackage(pkg.package_number)}
                  aria-disabled={shipmentSent}
                >
                  <Typography variant="body2" sx={{ fontWeight: 600 }}>{pkg.package_number}</Typography>
                  <Typography variant="caption" color="text.secondary">
                    {pkg.status}{pkg.user_email ? ` • ${pkg.user_email}` : ''}
                  </Typography>
                </Box>
              ))}
            </Box>
          )}

          {form.package_numbers.length === 0 ? (
            <Typography variant="body2" color="text.secondary">No packages added yet.</Typography>
          ) : (
            <>
              <Grid container spacing={1} sx={{ mb: 1, fontWeight: 600 }}>
                <Grid size={{ xs: 12, md: 10 }}><Typography variant="body2">Package Number</Typography></Grid>
                <Grid size={{ xs: 12, md: 2 }}><Typography variant="body2">Remove</Typography></Grid>
              </Grid>
              {form.package_numbers.map((pkgNum) => (
                <Grid container spacing={1} key={pkgNum} sx={{ mb: 1, alignItems: 'center' }}>
                  <Grid size={{ xs: 12, md: 10 }}>
                    <Typography variant="body2">{pkgNum}</Typography>
                  </Grid>
                  <Grid size={{ xs: 12, md: 2 }}>
                    <IconButton color="error" disabled={shipmentSent} onClick={() => removePackage(pkgNum)}>
                      <Delete />
                    </IconButton>
                  </Grid>
                </Grid>
              ))}
            </>
          )}

          <Stack direction="row" spacing={1} sx={{ mt: 3 }}>
            <Button variant="outlined" onClick={() => navigate('/admin/shipments')}>Cancel</Button>
            <Button variant="contained" disabled={!canSubmit || saving} onClick={submit}>
              {saving ? 'Saving...' : 'Save'}
            </Button>
          </Stack>
        </CardContent>
      </Card>
    </Box>
  );
}
